/**
 * The one-screen calendar's arithmetic (redesign spec, batch 1): the days a
 * player can book (today to the last day the server's booking window
 * reaches, in the player's zone), the phone's week pages and the Mac's
 * month grid, open times by day, and the lines the booking screens print.
 * Pure, so it is unit tested.
 */

/** A local calendar day, YYYY-MM-DD, in a zone. */
export function dayKey(iso: string, timeZone: string): string {
  return new Date(iso).toLocaleDateString('en-CA', { timeZone });
}

/** The day after a YYYY-MM-DD key (calendar arithmetic, no zone). */
function nextKey(key: string): string {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + 1)).toISOString().slice(0, 10);
}

/** Every local day from now to the latest bookable start, inclusive. */
export function windowDays(nowIso: string, latestIso: string, timeZone: string): string[] {
  const last = dayKey(latestIso, timeZone);
  const out: string[] = [];
  for (let k = dayKey(nowIso, timeZone); k <= last && out.length < 60; k = nextKey(k)) out.push(k);
  return out;
}

/** The phone's week strip: the window seven days at a time, from today. */
export function weekPages(days: string[]): string[][] {
  const pages: string[][] = [];
  for (let i = 0; i < days.length; i += 7) pages.push(days.slice(i, i + 7));
  return pages;
}

/** The Mac's month grid for YYYY-MM: Sunday first, padded with the neighbours. */
export function monthGrid(month: string): Array<Array<{ key: string; inMonth: boolean }>> {
  const [y, m] = month.split('-').map(Number);
  const first = new Date(Date.UTC(y, m - 1, 1));
  let cursor = new Date(Date.UTC(y, m - 1, 1 - first.getUTCDay()));
  const weeks: Array<Array<{ key: string; inMonth: boolean }>> = [];
  do {
    const week = [];
    for (let i = 0; i < 7; i += 1) {
      week.push({ key: cursor.toISOString().slice(0, 10), inMonth: cursor.getUTCMonth() === m - 1 });
      cursor = new Date(cursor.getTime() + 86_400_000);
    }
    weeks.push(week);
  } while (cursor.getUTCMonth() === m - 1);
  return weeks;
}

/** Open times grouped by local day, each day sorted by start. */
export function timesByDay<T extends { startAt: string }>(slots: T[], timeZone: string): Map<string, T[]> {
  const map = new Map<string, T[]>();
  for (const s of [...slots].sort((a, b) => a.startAt.localeCompare(b.startAt))) {
    const k = dayKey(s.startAt, timeZone);
    map.set(k, [...(map.get(k) ?? []), s]);
  }
  return map;
}

/** The first later day in the window that has open times, for the empty-day line. */
export function nextDayWithTimes(key: string, days: string[], byDay: Map<string, unknown[]>): string | null {
  return days.find((d) => d > key && (byDay.get(d)?.length ?? 0) > 0) ?? null;
}

/** Noon UTC on a day key, so weekday and date read the same in any zone. */
const noonOf = (key: string) => new Date(`${key}T12:00:00.000Z`);

/** "Tuesday", for a day key. */
export function weekdayOf(key: string): string {
  return noonOf(key).toLocaleDateString('en-US', { weekday: 'long', timeZone: 'UTC' });
}

/** "Tue", "19": a day chip's two lines. */
export function chipParts(key: string): { weekday: string; day: string; month: string } {
  const d = noonOf(key);
  return {
    weekday: d.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' }),
    day: d.toLocaleDateString('en-US', { day: 'numeric', timeZone: 'UTC' }),
    month: d.toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' }),
  };
}

/** "Sat, Oct 25": a day in the one booking screen's lines. */
export function shortDay(key: string): string {
  return noonOf(key).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });
}

/**
 * The first day past the booking window, in the coach's zone ("2026-11-07"):
 * the day Remind Me asks about. Today and the window's days are open whole,
 * so it is today plus the window's length plus one.
 */
export function firstDayPastWindow(nowMs: number, coachZone: string, maxDaysAhead: number): string {
  const today = new Date(nowMs).toLocaleDateString('en-CA', { timeZone: coachZone });
  return new Date(Date.parse(`${today}T12:00:00Z`) + (maxDaysAhead + 1) * 86_400_000).toISOString().slice(0, 10);
}

/** The one booking screen's days: today and the 13 after it, never past the window. */
export function bookingDays(nowIso: string, latestIso: string, timeZone: string, count = 14): string[] {
  return windowDays(nowIso, latestIso, timeZone).slice(0, count);
}

/**
 * The member's weekly time as an open time: the first open slot on its
 * weekday (0 is Sunday) at its time ("16:00"), read in the zone shown.
 */
export function usualSlot<T extends { startAt: string }>(
  slots: T[], weekly: { weekday: number; time: string } | null | undefined, timeZone: string,
): T | null {
  if (!weekly) return null;
  const hm = (iso: string) => new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone });
  return [...slots].sort((a, b) => a.startAt.localeCompare(b.startAt)).find((s) =>
    noonOf(dayKey(s.startAt, timeZone)).getUTCDay() === weekly.weekday && hm(s.startAt) === weekly.time) ?? null;
}

/** The zones offered before anything is typed, most players' first. */
export const COMMON_ZONES = [
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Phoenix', 'America/Los_Angeles',
  'America/Anchorage', 'Pacific/Honolulu', 'America/Toronto', 'America/Mexico_City', 'America/Sao_Paulo',
  'Europe/London', 'Europe/Paris', 'Europe/Madrid', 'Asia/Dubai', 'Asia/Kolkata', 'Asia/Shanghai',
  'Asia/Tokyo', 'Australia/Sydney',
];

/**
 * The inline zone list: the common zones with the current one first; typed
 * text narrows them by city or zone name and reaches every zone the browser
 * knows. At most 30 rows.
 */
export function zoneChoices(query: string, current: string): string[] {
  const common = [current, ...COMMON_ZONES.filter((z) => z !== current)];
  const q = query.trim().toLowerCase();
  if (!q) return common;
  // At the start of a word: "lon" is London, not Blanc-Sablon.
  const at = new RegExp(`(^|[^a-z])${q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`);
  const matches = (z: string) => at.test(zoneOptionLabel(z).toLowerCase()) || at.test(z.toLowerCase().replace(/_/g, ' '));
  const rest = zoneList(current).filter((z) => !common.includes(z));
  return [...common, ...rest].filter(matches).slice(0, 30);
}

/** "Tuesday, October 20": the open times' heading. */
export function dayTitle(key: string): string {
  return noonOf(key).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', timeZone: 'UTC' });
}

/** "1:00 PM". */
export function timeLabel(iso: string, timeZone: string): string {
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone });
}

/** "Mon, Oct 19 at 1:00 PM". */
export function whenLine(iso: string, timeZone: string): string {
  const d = new Date(iso);
  const date = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone });
  return `${date} at ${timeLabel(iso, timeZone)}`;
}

/** When a Match Review's match is due: a day before the lesson, as the server sets it. */
export function matchDueAt(startIso: string): string {
  return new Date(Date.parse(startIso) - 86_400_000).toISOString();
}

/**
 * The due time against now, so tonight's deadline never reads as next
 * week's: "7:00 PM today", "7:00 PM tomorrow", then "7:00 PM Sun".
 */
export function uploadDueLabel(dueIso: string, timeZone: string, nowMs: number): string {
  const due = dayKey(dueIso, timeZone);
  const today = dayKey(new Date(nowMs).toISOString(), timeZone);
  const tomorrow = dayKey(new Date(nowMs + 86_400_000).toISOString(), timeZone);
  const day = due === today ? 'today' : due === tomorrow ? 'tomorrow'
    : noonOf(due).toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' });
  return `${timeLabel(dueIso, timeZone)} ${day}`;
}

/** The day a Match Review's video is due: the day before the lesson ("Sunday"). */
export function uploadByDay(startIso: string, timeZone: string): string {
  const [y, m, d] = dayKey(startIso, timeZone).split('-').map(Number);
  return weekdayOf(new Date(Date.UTC(y, m - 1, d - 1)).toISOString().slice(0, 10));
}

/** "Eastern Time": the zone's generic name, else its id. */
export function zoneName(timeZone: string): string {
  try {
    return new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'longGeneric' })
      .formatToParts(new Date()).find((p) => p.type === 'timeZoneName')?.value ?? timeZone;
  } catch {
    return timeZone;
  }
}

/** A calendar file for the booked lesson, with the link to join. */
export function buildIcs(p: { uid: string; title: string; startAt: string; minutes: number; url: string; now?: string }): string {
  const stamp = (iso: string) => iso.replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  const end = new Date(new Date(p.startAt).getTime() + p.minutes * 60_000).toISOString();
  const lines = [
    'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Double Bagel//Live Video Lessons//EN', 'BEGIN:VEVENT',
    `UID:${p.uid}@doublebagel`, `DTSTAMP:${stamp(p.now ?? new Date().toISOString())}`,
    `DTSTART:${stamp(p.startAt)}`, `DTEND:${stamp(end)}`, `SUMMARY:${p.title}`,
    `DESCRIPTION:Join here: ${p.url}`, `URL:${p.url}`, 'END:VEVENT', 'END:VCALENDAR',
  ];
  return `${lines.join('\r\n')}\r\n`;
}

/** True for a zone this browser knows. */
export function knownZone(tz: string | null | undefined): tz is string {
  if (!tz) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/** "New York, Eastern Time": the city, then the zone's everyday name. */
export function zoneOptionLabel(tz: string): string {
  const city = tz.split('/').pop()?.replace(/_/g, ' ') ?? tz;
  const name = zoneName(tz);
  return name !== tz ? `${city}, ${name}` : city;
}

/** Every zone the browser lists, with the given one always among them. */
export function zoneList(current: string): string[] {
  let all: string[] = [];
  try {
    all = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.('timeZone') ?? [];
  } catch { /* an older browser: the current zone only */ }
  return all.includes(current) ? all : [current, ...all];
}

/** Downloads a lesson's calendar file, with the link to join. */
export function downloadLessonIcs(p: { bookingId: string; title: string; startAt: string; minutes: number }): void {
  const ics = buildIcs({
    uid: p.bookingId, title: p.title, startAt: p.startAt, minutes: p.minutes,
    url: `${window.location.origin}/call/${p.bookingId}`,
  });
  const url = URL.createObjectURL(new Blob([ics], { type: 'text/calendar' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = 'lesson.ics';
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * When the call opens, relative to now (decision 127): today "Opens at 1:20 AM", tomorrow "Opens tomorrow at
 * 1:20 AM", within six days the weekday "Opens Wednesday at 2:50 PM", beyond that the date "Opens Nov 12 at
 * 2:50 PM". Days are the viewer's calendar days.
 */
export function opensLine(opensAtMs: number, tz: string, nowMs: number): string {
  const key = (ms: number) => new Date(ms).toLocaleDateString('en-CA', { timeZone: tz });
  const days = Math.round((Date.parse(`${key(opensAtMs)}T12:00:00Z`) - Date.parse(`${key(nowMs)}T12:00:00Z`)) / 86_400_000);
  const at = new Date(opensAtMs);
  const time = at.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: tz });
  if (days <= 0) return `Opens at ${time}`;
  if (days === 1) return `Opens tomorrow at ${time}`;
  if (days <= 6) return `Opens ${at.toLocaleDateString('en-US', { weekday: 'long', timeZone: tz })} at ${time}`;
  return `Opens ${at.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: tz })} at ${time}`;
}

/** A row's date at the right of line 1 (decision 135): today the time, "Tomorrow", within six days "Wed", else "Nov 3". */
export function rowDate(startAt: string, tz: string, nowMs: number): string {
  const key = (ms: number) => new Date(ms).toLocaleDateString('en-CA', { timeZone: tz });
  const at = Date.parse(startAt);
  const days = Math.round((Date.parse(`${key(at)}T12:00:00Z`) - Date.parse(`${key(nowMs)}T12:00:00Z`)) / 86_400_000);
  if (days === 0) return new Date(at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: tz });
  if (days === 1) return 'Tomorrow';
  if (Math.abs(days) <= 6) return new Date(at).toLocaleDateString('en-US', { weekday: 'short', timeZone: tz });
  return new Date(at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: tz });
}
